import { Component, type ErrorInfo, type ReactNode } from 'react';
import { track } from '../lib/events.js';

/*
 * One broken part never blanks the app (pre-launch audit, 25 Oct 2026). Three levels:
 *  - the whole app (main.tsx): a plain page with Reload, which also lifts the intro's hold so it is never stuck on the logo;
 *  - each screen (App.tsx ScreenHost, keyed on the route): "This page didn't load" with Try again and Home, the tab bar stays;
 *  - each sheet (App.tsx Overlays): the sheet closes and a toast says so.
 * Every catch is counted as a 'client_error' event (the error's name and first line, where, the build), so a crash on
 * launch day shows up on Settings › Live checks instead of nowhere.
 */

export function reportClientError(where: string, error: unknown): void {
  try {
    const e = error instanceof Error ? error : new Error(String(error));
    track('client_error', { where, name: e.name, message: e.message.split('\n')[0]!.slice(0, 160), path: location.pathname.replace(/[0-9a-f]{16,}/gi, ':token').slice(0, 80) });
  } catch {
    /* never let reporting throw */
  }
}

interface Props {
  where: string;
  children: ReactNode;
  /** What to show instead; `reset` tries the children again. Omitted: nothing is shown. */
  fallback?: (reset: () => void) => ReactNode;
  /** A change (the route, the sheet) clears the error by itself. */
  resetKey?: unknown;
  onError?: (error: unknown) => void;
}

export class ErrorBoundary extends Component<Props, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error(`[${this.props.where}]`, error, info.componentStack);
    reportClientError(this.props.where, error);
    this.props.onError?.(error);
  }
  override componentDidUpdate(prev: Props): void {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }
  private reset = () => this.setState({ failed: false });
  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return this.props.fallback ? this.props.fallback(this.reset) : null;
  }
}

/** The whole app failed: a page that works with nothing else loaded (no store, no router, no theme). */
export function AppCrashed(): ReactNode {
  // The opening holds #root at opacity 0 under the launch mark until the intro lets it in: lift that, or this page is invisible.
  document.documentElement.removeAttribute('data-intro');
  document.getElementById('boot')?.remove();
  return (
    <div role="alert" style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, textAlign: 'center', background: '#f6f4ef', color: '#1d1a16', fontFamily: 'system-ui, sans-serif' }}>
      <b style={{ fontSize: 20 }}>Something went wrong</b>
      <p style={{ margin: 0, maxWidth: 320, lineHeight: 1.5 }}>Tikkas hit a problem showing this. Your order, points and credit are safe on our side.</p>
      <button type="button" className="yellow" style={{ minHeight: 56, padding: '0 28px', borderRadius: 999 }} onClick={() => location.reload()}>Reload</button>
    </div>
  );
}

/** One screen failed: the rest of the app (the tab bar, Show code) keeps working. */
export function ScreenCrashed({ reset, home }: { reset: () => void; home: () => void }): ReactNode {
  return (
    <div role="alert" className="col" style={{ minHeight: '60dvh', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24, textAlign: 'center' }}>
      <b style={{ fontSize: 18 }}>This page didn’t load</b>
      <p className="muted" style={{ margin: 0, maxWidth: 300, lineHeight: 1.5 }}>Something went wrong on our side. Your order, points and credit are safe.</p>
      <button type="button" className="yellow" style={{ minHeight: 56, padding: '0 28px', borderRadius: 999 }} onClick={reset}>Try again</button>
      <button type="button" className="link" style={{ minHeight: 44 }} onClick={home}>Go to Home</button>
    </div>
  );
}
